import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { MAX_UNDO_STEPS } from "../../../../config/newsletterEditorConfig";
import { createEmptyDocument, normalizeDocument } from "../model/documentModel";
import EditorMessageDialog from "../dialogs/EditorMessageDialog";
import { clearEditorSession, saveEditorSession } from "../utils/newsletterEditorSession";
import {
  addColumnToRow,
  addGridToLayout,
  addNestedGridToColumn,
  addRowToGrid,
  appendLayoutBlock,
  insertComponentInColumn,
  insertNestedLayoutInColumn,
  moveTopLevelLayoutGrid,
  clearColumnContent,
  removeNode,
  replaceDocumentMeta,
  resizeAdjacentColumns,
  setColumnWidthInRow,
  updateComponentProps,
  updateGridName,
  updateNodeProps,
  resizeGrid,
} from "../model/documentMutations";

const NewsletterEditorContext = createContext(null);

const normalizeSetDocumentOptions = (options) => {
  if (typeof options === "boolean") {
    return { markDirty: options, recordHistory: true };
  }
  return { markDirty: true, recordHistory: true, ...options };
};

export const NewsletterEditorProvider = ({
  initialDocument,
  initialObjectKey,
  initialWorkspaceActive = false,
  initialDirty = false,
  children,
}) => {
  const [document, setDocumentState] = useState(() => normalizeDocument(initialDocument || createEmptyDocument()));
  const [selection, setSelection] = useState(null);
  const [dirty, setDirty] = useState(initialDirty);
  const [savedObjectKey, setSavedObjectKey] = useState(initialObjectKey || null);
  const [workspaceActive, setWorkspaceActive] = useState(initialWorkspaceActive);
  const [messageDialog, setMessageDialog] = useState(null);
  const [imagesRevision, setImagesRevision] = useState(0);
  const [savedComponentSignal, setSavedComponentSignal] = useState(null);
  const [inlineEditComponentId, setInlineEditComponentId] = useState(null);
  const historyRef = useRef([]);
  const futureRef = useRef([]);

  const activateWorkspace = useCallback(() => {
    setWorkspaceActive(true);
  }, []);

  const showEditorMessage = useCallback(({ title, message, detail, confirmText, accentColor }) => {
    setMessageDialog({ title, message, detail: detail || null, confirmText, accentColor });
  }, []);

  const closeEditorMessage = useCallback(() => setMessageDialog(null), []);

  const refreshImageLibrary = useCallback(() => {
    setImagesRevision((value) => value + 1);
  }, []);

  const publishSavedComponent = useCallback((summary, payload) => {
    if (!summary?.objectKey || !payload) {
      return;
    }
    setSavedComponentSignal({ summary, payload, token: Date.now() });
  }, []);

  const clearSavedComponentSignal = useCallback(() => {
    setSavedComponentSignal(null);
  }, []);

  const requestInlineTextEdit = useCallback((componentId) => {
    setInlineEditComponentId(componentId);
    setSelection({ type: "component", id: componentId });
  }, []);

  const clearInlineTextEdit = useCallback(() => {
    setInlineEditComponentId(null);
  }, []);

  const pushHistory = useCallback((snapshot) => {
    historyRef.current = [...historyRef.current.slice(-MAX_UNDO_STEPS + 1), snapshot];
    futureRef.current = [];
  }, []);

  const setDocument = useCallback(
    (updater, options = true) => {
      const normalized = normalizeSetDocumentOptions(options);
      setDocumentState((current) => {
        if (normalized.recordHistory) {
          pushHistory(current);
        }
        const next = typeof updater === "function" ? updater(current) : updater;
        if (normalized.markDirty) {
          setDirty(true);
        }
        return next;
      });
    },
    [pushHistory]
  );

  const undo = useCallback(() => {
    if (!historyRef.current.length) {
      return;
    }
    const previous = historyRef.current.at(-1);
    historyRef.current = historyRef.current.slice(0, -1);
    setDocumentState((current) => {
      futureRef.current = [...futureRef.current, current];
      setDirty(true);
      setSelection(null);
      return previous;
    });
  }, []);

  const redo = useCallback(() => {
    if (!futureRef.current.length) {
      return;
    }
    const next = futureRef.current.at(-1);
    futureRef.current = futureRef.current.slice(0, -1);
    setDocumentState((current) => {
      historyRef.current = [...historyRef.current, current];
      setDirty(true);
      setSelection(null);
      return next;
    });
  }, []);

  const resetDocument = useCallback((nextDocument) => {
    historyRef.current = [];
    futureRef.current = [];
    setDocumentState(normalizeDocument(nextDocument || createEmptyDocument()));
    setSelection(null);
    setDirty(false);
    setSavedObjectKey(null);
    clearEditorSession();
  }, []);

  useEffect(() => {
    if (!workspaceActive) {
      return;
    }
    saveEditorSession({
      document,
      savedObjectKey,
      dirty,
      workspaceActive,
    });
  }, [document, savedObjectKey, dirty, workspaceActive]);

  const value = useMemo(
    () => ({
      document,
      selection,
      dirty,
      savedObjectKey,
      workspaceActive,
      imagesRevision,
      savedComponentSignal,
      setSelection,
      setDirty,
      setSavedObjectKey,
      activateWorkspace,
      setDocument,
      resetDocument,
      undo,
      redo,
      showEditorMessage,
      closeEditorMessage,
      refreshImageLibrary,
      publishSavedComponent,
      clearSavedComponentSignal,
      inlineEditComponentId,
      requestInlineTextEdit,
      clearInlineTextEdit,
      replaceMeta: (patch) => setDocument((current) => replaceDocumentMeta(current, patch)),
      insertComponent: (columnId, componentKey, propsOverride, options = {}) => {
        const { document: next, result } = insertComponentInColumn(
          document,
          columnId,
          componentKey,
          propsOverride,
          options
        );
        if (result === "inserted" || result === "replaced") {
          setDocument(() => next);
        }
        return result;
      },
      insertLayoutInColumn: (columnId, savedGrid, options = {}) => {
        const { document: next, result } = insertNestedLayoutInColumn(document, columnId, savedGrid, options);
        if (result === "inserted" || result === "replaced") {
          setDocument(() => next);
        }
        return result;
      },
      updateProps: (nodeId, patch, isComponent = false, options = {}) =>
        setDocument(
          (current) =>
            isComponent ? updateComponentProps(current, nodeId, patch) : updateNodeProps(current, nodeId, patch),
          options
        ),
      clearColumnContent: (columnId) =>
        setDocument((current) => clearColumnContent(current, columnId ?? selection?.id)),
      removeSelected: (nodeId) => {
        setDocument((current) => removeNode(current, nodeId ?? selection?.id));
        setSelection(null);
      },
      appendLayout: (grid) => setDocument((current) => appendLayoutBlock(current, grid)),
      updateGridName: (gridId, name) => setDocument((current) => updateGridName(current, gridId, name)),
      moveLayoutGrid: (gridId, direction) =>
        setDocument((current) => moveTopLevelLayoutGrid(current, gridId, direction)),
      addGridToLayout: (rowCount, columnCount) =>
        setDocument((current) => addGridToLayout(current, rowCount, columnCount)),
      addNestedGrid: (columnId, rowCount, columnCount) =>
        setDocument((current) => addNestedGridToColumn(current, columnId, rowCount, columnCount)),
      addRow: (gridId) => setDocument((current) => addRowToGrid(current, gridId)),
      addColumnToRow: (rowId) => setDocument((current) => addColumnToRow(current, rowId)),
      resizeColumns: (rowId, leftId, rightId, leftWidth, rightWidth) =>
        setDocument((current) => resizeAdjacentColumns(current, rowId, leftId, rightId, leftWidth, rightWidth)),
      setColumnWidth: (rowId, columnId, width) =>
        setDocument((current) => setColumnWidthInRow(current, rowId, columnId, width)),
      resizeGrid: (gridId, rowCount, columnCount) =>
        setDocument((current) => resizeGrid(current, gridId, rowCount, columnCount)),
      loadDocument: (loaded, objectKey) => {
        resetDocument(loaded);
        setSavedObjectKey(objectKey);
        activateWorkspace();
      },
    }),
    [
      document,
      selection,
      dirty,
      savedObjectKey,
      workspaceActive,
      imagesRevision,
      savedComponentSignal,
      inlineEditComponentId,
      setDocument,
      resetDocument,
      activateWorkspace,
      undo,
      redo,
      showEditorMessage,
      closeEditorMessage,
      refreshImageLibrary,
      publishSavedComponent,
      clearSavedComponentSignal,
      requestInlineTextEdit,
      clearInlineTextEdit,
    ]
  );

  return (
    <NewsletterEditorContext.Provider value={value}>
      {children}
      <EditorMessageDialog
        open={Boolean(messageDialog)}
        onClose={closeEditorMessage}
        title={messageDialog?.title || ""}
        message={messageDialog?.message || ""}
        detail={messageDialog?.detail}
        confirmText={messageDialog?.confirmText}
        accentColor={messageDialog?.accentColor}
      />
    </NewsletterEditorContext.Provider>
  );
};

NewsletterEditorProvider.propTypes = {
  initialDocument: PropTypes.object,
  initialObjectKey: PropTypes.string,
  initialWorkspaceActive: PropTypes.bool,
  initialDirty: PropTypes.bool,
  children: PropTypes.node.isRequired,
};

export const useNewsletterEditor = () => {
  const context = useContext(NewsletterEditorContext);
  if (!context) {
    throw new Error("useNewsletterEditor must be used within NewsletterEditorProvider");
  }
  return context;
};
 